import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  social: boolean;
}

const DEFAULT_PREFS: CookiePreferences = {
  essential: true,
  analytics: false,
  social: false,
};

const STORAGE_KEY = 'mg_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFS);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Small delay so the banner slides in after page load
      const t = setTimeout(() => {
        setVisible(true);
        setMounted(true);
      }, 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const save = (accepted: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...accepted, timestamp: Date.now() }));
    setVisible(false);
  };

  const acceptAll = () => save({ essential: true, analytics: true, social: true });
  const rejectAll = () => save({ essential: true, analytics: false, social: false });
  const saveCustom = () => save(prefs);

  const togglePref = (key: keyof Omit<CookiePreferences, 'essential'>) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!mounted) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500 ease-out ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {/* Backdrop blur strip */}
      <div className="bg-white/95 backdrop-blur-sm border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5">

          {/* Compact view */}
          {!expanded && (
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5">
              {/* Icon + text */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1B3A5C]/8 flex-shrink-0 mt-0.5">
                  <i className="ri-cookie-line text-[#1B3A5C] text-lg" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1B3A5C] mb-0.5">Usamos cookies</p>
                  <p className="text-xs text-gray-500 leading-relaxed max-w-2xl">
                    Utilizamos cookies propias y de terceros para mejorar tu experiencia de navegación y analizar el uso del sitio. Puedes aceptarlas todas, rechazar las no esenciales o personalizar tu elección.{' '}
                    <Link
                      to="/politica-de-cookies"
                      className="text-[#3D9E8C] hover:underline font-medium whitespace-nowrap"
                    >
                      Más información
                    </Link>
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2.5 flex-shrink-0 flex-wrap">
                <button
                  onClick={() => setExpanded(true)}
                  className="px-4 py-2 text-xs font-medium text-[#1B3A5C] border border-[#1B3A5C]/20 rounded-full hover:bg-[#1B3A5C]/5 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Personalizar
                </button>
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Rechazar
                </button>
                <button
                  onClick={acceptAll}
                  className="px-5 py-2 text-xs font-semibold text-white bg-[#3D9E8C] rounded-full hover:bg-[#2d8a78] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          )}

          {/* Expanded / customize view */}
          {expanded && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1B3A5C]/8 flex-shrink-0">
                    <i className="ri-settings-4-line text-[#1B3A5C] text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1B3A5C]">Personalizar preferencias de cookies</p>
                    <p className="text-xs text-gray-400">Activa o desactiva cada categoría según tu preferencia.</p>
                  </div>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer text-gray-400 flex-shrink-0"
                >
                  <i className="ri-close-line text-base" />
                </button>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Essential */}
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1B3A5C]">Cookies esenciales</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">Necesarias para el funcionamiento del sitio. No se pueden desactivar.</p>
                  </div>
                  <div className="w-8 h-4 rounded-full bg-[#3D9E8C] flex items-center justify-end pr-0.5 flex-shrink-0 mt-1">
                    <span className="w-3 h-3 rounded-full bg-white block" />
                  </div>
                </div>

                {/* Analytics */}
                <div
                  className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3.5 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => togglePref('analytics')}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1B3A5C]">Cookies analíticas</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">Nos ayudan a entender cómo se utiliza el sitio (Google Analytics).</p>
                  </div>
                  <button
                    className={`w-8 h-4 rounded-full transition-colors duration-200 flex items-center flex-shrink-0 mt-1 ${
                      prefs.analytics ? 'bg-[#3D9E8C] justify-end pr-0.5' : 'bg-gray-300 justify-start pl-0.5'
                    }`}
                    aria-label="Toggle analytics"
                  >
                    <span className="w-3 h-3 rounded-full bg-white block" />
                  </button>
                </div>

                {/* Social */}
                <div
                  className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3.5 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => togglePref('social')}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1B3A5C]">Cookies de redes sociales</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">Permiten compartir contenido en LinkedIn, Twitter y YouTube.</p>
                  </div>
                  <button
                    className={`w-8 h-4 rounded-full transition-colors duration-200 flex items-center flex-shrink-0 mt-1 ${
                      prefs.social ? 'bg-[#3D9E8C] justify-end pr-0.5' : 'bg-gray-300 justify-start pl-0.5'
                    }`}
                    aria-label="Toggle social"
                  >
                    <span className="w-3 h-3 rounded-full bg-white block" />
                  </button>
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1 border-t border-gray-100">
                <Link
                  to="/politica-de-cookies"
                  className="text-xs text-[#3D9E8C] hover:underline whitespace-nowrap"
                >
                  Ver política de cookies completa
                </Link>
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={rejectAll}
                    className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Rechazar todas
                  </button>
                  <button
                    onClick={saveCustom}
                    className="px-5 py-2 text-xs font-semibold text-white bg-[#1B3A5C] rounded-full hover:bg-[#152d4a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Guardar preferencias
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2 text-xs font-semibold text-white bg-[#3D9E8C] rounded-full hover:bg-[#2d8a78] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Aceptar todas
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
